import { getErrorMessage, getErrorType, ERROR_TYPES, ERROR_MESSAGES } from "@/constants/errorMessages";

/**
 * Centralized Error Service
 * Handles all application errors with consistent behavior and messaging
 */
class ErrorService {
  constructor() {
    this.errorHandlers = new Map();
    this.errorHistory = [];
    this.maxHistorySize = 100;

    // Initialize default handlers
    this.initializeDefaultHandlers();
  }

  /**
   * Initialize default error handlers for different error types
   */
  initializeDefaultHandlers() {
    this.errorHandlers.set(ERROR_TYPES.NETWORK, this.handleNetworkError.bind(this));
    this.errorHandlers.set(ERROR_TYPES.AUTH, this.handleAuthError.bind(this));
    this.errorHandlers.set(ERROR_TYPES.SERVER, this.handleServerError.bind(this));
    this.errorHandlers.set(ERROR_TYPES.VALIDATION, this.handleValidationError.bind(this));
    this.errorHandlers.set(ERROR_TYPES.BUSINESS, this.handleBusinessError.bind(this));
    this.errorHandlers.set(ERROR_TYPES.UNKNOWN, this.handleUnknownError.bind(this));
  }

  /**
   * Main error handling method
   * @param {Error} error - The error object
   * @param {Object} context - Additional context information
   */
  handleError(error, context = {}) {
    try {
      const errorType = getErrorType(error);
      const errorMessage = getErrorMessage(error);

      // Log error for debugging
      this.logError(error, errorType, context);

      // Get appropriate handler and execute
      const handler = this.errorHandlers.get(errorType) || this.handleUnknownError;
      return handler(error, errorMessage, context);

    } catch (handlingError) {
      console.error("Error in error handling:", handlingError);
      // Fallback to basic error display
      this.showErrorToast("danger", ERROR_MESSAGES.SYSTEM_ERROR);
    }
  }

  /**
   * Handle network errors
   */
  handleNetworkError(error, message, context) {
    this.showErrorToast(
      "warning",
      ERROR_MESSAGES.NETWORK_ERROR_EN,
      false // Auto-hide after 8 seconds instead of staying forever
    );

    return {
      type: ERROR_TYPES.NETWORK,
      message: ERROR_MESSAGES.NETWORK_ERROR,
      handled: true
    };
  }

  /**
   * Handle authentication errors
   */
  handleAuthError(error, message, context) {
    const statusCode = error.response?.data?.errorCode || error.response?.status;

    if (statusCode === 401) {
      // Show session timeout modal
      this.showTimeoutSession();
    } else if (statusCode === 403) {
      // Phase 1C: a 403 likely means permissions changed mid-session and the
      // local context cache is stale. Prompt the user to refresh once; debounce
      // so a burst of 403s doesn't spam modals.
      this.handleStalePermissions(error, message);
    } else {
      this.showErrorToast("warning", message);
    }

    return {
      type: ERROR_TYPES.AUTH,
      message,
      handled: true
    };
  }

  /**
   * Phase 1C: prompt user to refresh stale permission cache after 403.
   * Debounced — only fires once per 10 seconds.
   */
  handleStalePermissions(error, message) {
    const now = Date.now();
    if (this._lastStalePromptAt && now - this._lastStalePromptAt < 10000) {
      // Already prompted recently; just toast the underlying message.
      this.showErrorToast("warning", message);
      return;
    }
    this._lastStalePromptAt = now;

    const app = this.appInstance;
    if (!app || !app.$bvModal || !app.$store) {
      // No UI available — fallback to toast.
      this.showErrorToast("warning", message);
      return;
    }

    app.$bvModal
      .msgBoxConfirm(
        "สิทธิ์การเข้าถึงของคุณมีการเปลี่ยนแปลง กรุณา refresh สิทธิ์ใหม่",
        {
          title: "สิทธิ์การเข้าถึงเปลี่ยน",
          okTitle: "Refresh สิทธิ์",
          cancelTitle: "ยกเลิก",
          centered: true,
        }
      )
      .then(ok => {
        if (!ok) return;
        const clinicURL = app.$store.state.moduleUser?.branchInfo?.clinic?.code;
        if (!clinicURL) return;
        app.$store
          .dispatch("moduleUser/loadPermissionContext", { clinicURL })
          .catch(() => {
            // Swallow — original error already surfaced.
          });
      })
      .catch(() => {
        // Modal dismissed; nothing to do.
      });
  }

  /**
   * Handle server errors
   */
  handleServerError(error, message, context) {
    this.showErrorToast("danger", message);

    return {
      type: ERROR_TYPES.SERVER,
      message,
      handled: true
    };
  }

  /**
   * Handle validation errors
   */
  handleValidationError(error, message, context) {
    // Pass through original App.vue format: error.message + backend message
    const errorMessage = error.response?.data?.errorMessage;
    const originalFormat = `${error.message} \n${errorMessage?.validationResult?.[ 0 ]?.error?.[ 0 ]?.path ||
      errorMessage || ''
      }`;

    this.showErrorToast("danger", originalFormat);

    return {
      type: ERROR_TYPES.VALIDATION,
      message: originalFormat,
      handled: true
    };
  }

  /**
   * Handle business logic errors
   */
  handleBusinessError(error, message, context) {
    // Pass through original App.vue format: error.message + backend message
    const errorMessage = error.response?.data?.errorMessage;
    const originalFormat = `${error.message} \n${errorMessage?.validationResult?.[ 0 ]?.error?.[ 0 ]?.path ||
      errorMessage || ''
      }`;

    this.showErrorToast("danger", originalFormat);

    return {
      type: ERROR_TYPES.BUSINESS,
      message: originalFormat,
      handled: true
    };
  }

  /**
   * Handle unknown errors
   */
  handleUnknownError(error, message, context) {
    // Pass through original App.vue format: error.message + backend message
    const errorMessage = error.response?.data?.errorMessage;
    const originalFormat = `${error.message} \n${errorMessage?.validationResult?.[ 0 ]?.error?.[ 0 ]?.path ||
      errorMessage || ''
      }`;

    this.showErrorToast("danger", originalFormat);

    return {
      type: ERROR_TYPES.UNKNOWN,
      message: originalFormat,
      handled: true
    };
  }

  /**
   * Extract detailed validation message from error response
   * @param {Error} error - The error object
   * @returns {string|null} - Formatted validation message or null
   */
  extractValidationMessage(error) {
    const errorData = error.response?.data?.errorMessage;

    if (errorData?.validationResult?.[ 0 ]?.error?.[ 0 ]?.path) {
      return errorData.validationResult[ 0 ].error[ 0 ].path;
    }

    if (typeof errorData === 'string') {
      return errorData;
    }

    return null;
  }

  /**
   * Log error for debugging and monitoring
   * @param {Error} error - The error object
   * @param {string} errorType - Error type
   * @param {Object} context - Additional context
   */
  logError(error, errorType, context) {
    const errorLog = {
      timestamp: new Date().toISOString(),
      type: errorType,
      message: error.message,
      status: error.response?.status,
      url: error.config?.url,
      method: error.config?.method,
      context,
      stack: error.stack
    };

    // Add to error history
    this.errorHistory.unshift(errorLog);
    if (this.errorHistory.length > this.maxHistorySize) {
      this.errorHistory.pop();
    }

    // Console log in development
    if (process.env.NODE_ENV === 'development') {
      console.group(`🚨 ${errorType.toUpperCase()} ERROR`);
      console.error("Error:", error);
      console.log("Context:", context);
      console.log("Full Log:", errorLog);
      console.groupEnd();
    }

    // TODO: Send to monitoring service in production
    // this.sendToMonitoring(errorLog);
  }

  /**
   * Show error toast using the app's Alert component
   */
  showErrorToast(variant, message, noAutoHide = false) {
    if (this.appInstance && this.appInstance.$refs && this.appInstance.$refs.Alert) {
      this.appInstance.$refs.Alert.showToast(variant, message, noAutoHide);
    } else {
      // Fallback to console for debugging if UI is not available
      console.warn(`Error Toast (${variant}):`, message);
    }
  }

  /**
   * Show session timeout modal
   */
  showTimeoutSession() {
    if (this.appInstance && this.appInstance.$refs && this.appInstance.$refs.Alert) {
      this.appInstance.$refs.Alert.showTimeoutSession();
    } else {
      console.warn("Session timeout - UI component not available");
    }
  }

  /**
   * Register custom error handler for specific error types
   * @param {string} errorType - Error type
   * @param {Function} handler - Error handler function
   */
  registerHandler(errorType, handler) {
    this.errorHandlers.set(errorType, handler);
  }

  /**
   * Get error history for debugging
   * @param {number} limit - Number of recent errors to return
   * @returns {Array} - Array of error logs
   */
  getErrorHistory(limit = 10) {
    return this.errorHistory.slice(0, limit);
  }

  /**
   * Clear error history
   */
  clearErrorHistory() {
    this.errorHistory = [];
  }

  /**
   * Check if error should be suppressed (for duplicate errors)
   * @param {Error} error - The error object
   * @returns {boolean} - Whether error should be suppressed
   */
  shouldSuppressError(error) {
    // Check for duplicate errors in the last 5 seconds
    const now = Date.now();
    const recentErrors = this.errorHistory
      .filter(log => now - new Date(log.timestamp).getTime() < 5000)
      .filter(log => log.message === error.message && log.url === error.config?.url);

    return recentErrors.length > 0;
  }

  /**
   * Set UI reference for direct UI interactions
   * @param {Object} appInstance - Vue app instance reference
   */
  setAppReference(appInstance) {
    this.appInstance = appInstance;
    // Also set global reference for backward compatibility
    window.app = appInstance;
  }
}

// Create singleton instance
const errorService = new ErrorService();

// Export for use in axios interceptor and other modules
export default errorService;

// Export class for testing
export { ErrorService };